import React, { ComponentProps, FC, useCallback, useContext, useEffect, useMemo } from 'react';
import { HostnameSelect } from '../HostnameSelect';
import {
  Box,
  Button,
  DeprecatedIcon,
  Flex,
  IconButton,
  InputDescription,
  Pane,
  Text,
  Tooltip,
} from '@fullstory/components';
import { PatternRuleForm } from '../../types';
import {
  AutocompleteRequest,
  InvalidPattern,
  ValidationError,
} from '@fullstory/proto/fs/services/paige';
import { RuleWarning } from '../RuleWarning';
import { isEqual } from 'lodash';
import { FormattedMessage, useIntl } from 'react-intl';
import { intl } from '@fullstory/intl';
import { Event, UrlCategory } from '@fullstory/proto/fs/api/segment';
import { ConflictingRules } from '../ConflictingRules';
import { PageDefinition } from '@fullstory/proto/fs/api/pages';
import {
  ActiveIndexContext,
  AppearancesContext,
  appearancesProperty,
} from '@fullstory/signal-studio-context';
import classNames from 'classnames';
import css from './index.module.scss';
import { makePagePatternDeps } from '../../utils/signal-studio';
import { FullstoryManaged } from '../FullstoryManaged';
import { UrlTooltip } from '@fullstory/library-ui';
import { useDebounce, useFlags } from '@fullstory/react-hooks';
import { OrControl } from '@fullstory/search-shared-ui';
import { URLRuleQueryParam } from '@fullstory/pages-shared/components/URLRuleQueryParam';
import { URLRuleFragment } from '@fullstory/pages-shared/components/URLRuleFragment';
import { URLRuleAutoComplete } from '@fullstory/pages-shared/components/URLRuleAutoComplete';
import { AppDispatch, SearchAsyncActions } from '@fullstory/redux';
import { useDispatch } from 'react-redux';
import { CompletionGroup, FieldSuggestRequest } from '@fullstory/proto/fs/services/alexa';
import { Aggregation, UniqueAggregation } from '@fullstory/proto/fs/api/metrics';
import { EMPTY_PATTERN } from '../../constants';

const appearancesPropertyOptions: appearancesProperty[] = [
  'path',
  'host',
  'url',
  'page',
  'device',
  'queryParam',
];

export interface URLRulesPatternsProps {
  value: PatternRuleForm[];
  onChange: (value: PatternRuleForm[]) => void;
  hostnameOptions: string[];
  getSuggestions: (
    path: string,
    fragment: string,
    hostname: string,
    scope: AutocompleteRequest.Scope,
  ) => Promise<string[]>;
  isML?: boolean;
  validationErrors?: ValidationError[];
  onConflictingRulesEdit: (pageId: number | null) => void;
  onConflictingRulesArchive: (id: number, archive: boolean) => void;
  errors?: ValidationError[];
  getPageById: (pageDefId: number) => PageDefinition | undefined;
  isCaseInsensitive?: boolean;
  preventOverflow?: boolean;
}

const errorMessage: { [key in InvalidPattern.Reason]: string } = {
  [InvalidPattern.Reason.MISSING_PATTERN]: intl.formatMessage({
    defaultMessage: 'This field must not be empty.',
    id: 'eH3/Ar',
  }),
  [InvalidPattern.Reason.DOMAIN_GLOB]: intl.formatMessage({
    defaultMessage: 'Domain name is only supported as first portion.',
    id: 'Nsk9Rj',
  }),
  [InvalidPattern.Reason.PATH_GLOB]: intl.formatMessage({
    defaultMessage: '"**" is only supported as last portion.',
    id: 'fbvSxw',
  }),
  [InvalidPattern.Reason.FRAGMENT_GLOB]: intl.formatMessage({
    defaultMessage: '"**" is only supported as a last portion.',
    id: '+hR92H',
  }),
  [InvalidPattern.Reason.QUERY_GLOB]: intl.formatMessage({
    defaultMessage: '"**" is not supported for query parameters.',
    id: '8eIF2y',
  }),
  [InvalidPattern.Reason.NESTED_GROUP]: intl.formatMessage({
    defaultMessage: 'Nested group is incorrect.',
    id: 'Mp9xid',
  }),
  [InvalidPattern.Reason.GLOB_IN_GROUP]: intl.formatMessage({
    defaultMessage: '"**" is incorrect.',
    id: '6ayqj2',
  }),
  [InvalidPattern.Reason.WILDCARD_IN_GROUP]: intl.formatMessage({
    defaultMessage: 'Wildcard is incorrect.',
    id: 'cBTqlY',
  }),
  [InvalidPattern.Reason.INVALID_CHARACTER_GROUP]: intl.formatMessage({
    defaultMessage: 'There’s an error in one or more characters.',
    id: 'eyO7W/',
  }),
  [InvalidPattern.Reason.EMPTY_LITERAL]: intl.formatMessage({
    defaultMessage: 'This field must not be empty.',
    id: 'eH3/Ar',
  }),
  [InvalidPattern.Reason.GROUP_IN_LITERAL]: intl.formatMessage({
    defaultMessage: 'Nested group is incorrect.',
    id: 'Mp9xid',
  }),
  [InvalidPattern.Reason.PARTIAL_WILDCARD]: intl.formatMessage({
    defaultMessage: 'Wildcard is incorrect.',
    id: 'cBTqlY',
  }),
  [InvalidPattern.Reason.INVALID_STRUCTURE]: intl.formatMessage({
    defaultMessage: 'URL structure is incorrect.',
    id: '1BqZS1',
  }),
  [InvalidPattern.Reason.DUPLICATE_IN_GROUP]: intl.formatMessage({
    defaultMessage: 'Duplicate portion in [] group.',
    id: '/3eSSX',
  }),
  [InvalidPattern.Reason.INVALID_ESCAPE]: intl.formatMessage({
    defaultMessage: '"\\" is not not allowed.',
    id: 'eD05zE',
  }),
  [InvalidPattern.Reason.COMMA_IN_TERM]: intl.formatMessage({
    defaultMessage: '"," is not allowed.',
    id: 'aZ+XLg',
  }),
  [InvalidPattern.Reason.QUESTION_MARK_IN_TERM]: intl.formatMessage({
    defaultMessage: '"?" is not allowed.',
    id: 'ife1AK',
  }),
};

const getValidationErrorForRule = (
  rule: PatternRuleForm,
  validationErrors: ValidationError[] = [],
): string | undefined => {
  for (const validationError of validationErrors) {
    const patternRule = validationError.invalidPattern?.patternRule;
    const invalidPattern = validationError?.invalidPattern;
    if (
      invalidPattern?.reason &&
      patternRule &&
      isEqual(patternRule.domains, rule.domains.parsed) &&
      isEqual(patternRule.path, rule.path.parsed) &&
      isEqual(patternRule.fragment, rule.fragment.parsed) &&
      isEqual(patternRule.query, rule.query.parsed)
    ) {
      return errorMessage[invalidPattern.reason];
    }
  }
  return undefined;
};

type URLRowProps = {
  idx: number;
  rule: PatternRuleForm;
  onChange: (value: PatternRuleForm) => void;
  onRemove: (() => void) | undefined;
  hostnameOptions: string[];
  getSuggestions: (
    path: string,
    fragment: string,
    hostname: string,
    scope: AutocompleteRequest.Scope,
  ) => Promise<string[]>;
  validationErrors?: ValidationError[];
  isML?: boolean;
  isCaseInsensitive?: boolean;
  preventOverflow?: boolean;
};

type URLRowAtIndexProps = Omit<URLRowProps, 'onChange' | 'onRemove'> & {
  value: PatternRuleForm[];
  onChange: (value: PatternRuleForm[]) => void;
};

const URLRuleRowAtIndex: React.FC<URLRowAtIndexProps> = ({ idx, value, onChange, ...rest }) => {
  const onChangeForIdx = useCallback(
    (rule: PatternRuleForm) => {
      const newValue = [...value];
      newValue[idx] = rule;
      onChange(newValue);
    },
    [idx, value, onChange],
  );
  const onRemoveForIdx = useMemo(() => {
    if (value.length <= 1) {
      return undefined;
    }
    return () => {
      const newValue = [...value];
      newValue.splice(idx, 1);
      onChange(newValue);
    };
  }, [value, onChange]);

  return <URLRuleRow {...rest} idx={idx} onChange={onChangeForIdx} onRemove={onRemoveForIdx} />;
};

export const URLRuleRow: React.FC<URLRowProps> = ({
  idx,
  rule,
  onChange,
  onRemove,
  hostnameOptions,
  getSuggestions,
  validationErrors,
  isML,
  isCaseInsensitive,
  preventOverflow,
}) => {
  const flags = useFlags();
  const hasFragmentEnabled = flags.featureFlags().AppPageFragmentGrouping;
  const dispatch = useDispatch<AppDispatch>();
  const appearances = useContext(AppearancesContext);
  const { Appearances, onPreview } = { ...appearances };

  const activePattern = useContext(ActiveIndexContext);
  const active = activePattern?.index == idx;
  const setActive = useCallback(() => activePattern?.setIndex(idx), [activePattern, idx]);

  const handleHostnameChange = (domains: string | undefined) => {
    if (domains === undefined) return;
    onChange({
      ...rule,
      domains: { value: domains },
    });
  };

  const [hasFragmentInPath, setHasFragmentInPath] = React.useState(false);
  const [pathFieldFocused, setPathFieldFocused] = React.useState(false);

  const handlePathChange = useCallback(
    (rawPath: string) => {
      // for ease of use, backwards compatibility, and better auto complete handling, we will still handle fragments in the path field
      // once the field is blurred, the fragment will get moved over to the separate field
      const hasFragmentInRawPath = rawPath.includes('#');
      const [path, fragment] = hasFragmentInRawPath
        ? rawPath.split('#')
        : [rawPath, rule.fragment.value];
      onChange({
        ...rule,
        path: { value: path ?? '' },
        fragment: { value: fragment ?? '' },
      });
      setHasFragmentInPath(hasFragmentInRawPath);
    },
    [rule, onChange],
  );

  const showFragmentInPath = hasFragmentInPath && (pathFieldFocused || !hasFragmentEnabled);
  const pathValue = showFragmentInPath
    ? `${rule.path.value}#${rule.fragment.value}`
    : rule.path.value;

  const getPathSuggestions = useCallback(
    (value: string): Promise<string[]> => {
      // intentionally passing empty fragment (or fragment bits *explicitly* typed in path field) to get all path suggestions
      // the fragment suggestions are dependent on the path, but let's not make the reverse true here
      // and even though the fragment field is broken out, let's get suggestions including fragment for simplicity
      const [path, fragment] = value.split('#');
      return getSuggestions(
        path ?? '',
        fragment ?? '',
        rule.domains.value,
        AutocompleteRequest.Scope.SCOPE_PATH_FRAGMENT,
      );
    },
    [getSuggestions, rule.domains.value],
  );

  const handlePathFocus = useCallback(() => {
    setPathFieldFocused(true);
    setHasFragmentInPath(false);
    setActive();
  }, [setActive]);
  const handlePathBlur = useCallback(() => {
    setPathFieldFocused(false);
  }, []);

  const canAddQueryParam =
    flags.featureFlags().AppPageDefinitionQueryParams && !rule.query.value.length;
  const handleAddQueryParam = useCallback(() => {
    onChange({
      ...rule,
      query: { value: [{ key: '', value: '' }] },
    });
  }, [rule, onChange]);

  const onChangeQueryParam = useCallback(
    (queryParamIndex: number, queryParamValue: { key: string; value: string }) => {
      const existingQueryParamAtIndex = rule.query.value[queryParamIndex];
      if (existingQueryParamAtIndex) {
        const newQueryValue = [...rule.query.value];
        newQueryValue[queryParamIndex] = queryParamValue;
        onChange({
          ...rule,
          query: { value: newQueryValue },
        });
      }
    },
    [rule, onChange],
  );

  const onClearQueryParam = useCallback(
    (queryParamIndex: number) => {
      const newQueryValue = [...rule.query.value];
      newQueryValue.splice(queryParamIndex, 1);
      onChange({
        ...rule,
        query: { value: newQueryValue },
      });
    },
    [rule, onChange],
  );

  const getQuerySuggestions = useCallback(
    async (prefix: string, mapCompletion: (completion: string) => string): Promise<string[]> => {
      const req: Partial<Pick<FieldSuggestRequest, 'eventFilter'>> = {
        eventFilter: {
          visitedUrl: {
            containsTokenStartsWith: {
              value: [{ value: prefix, category: UrlCategory.URL_STANDARD_QUERY }],
            },
          },
        },
      };
      const groups: CompletionGroup[] = await dispatch(
        SearchAsyncActions.autocomplete(req, prefix),
      );
      const suggestions: string[] = [];
      groups.forEach(group => {
        group.completions.forEach(completion => {
          const suggestion = mapCompletion(completion.text);
          if (suggestion && !suggestions.includes(suggestion)) {
            suggestions.push(suggestion);
          }
        });
      });
      return suggestions;
    },
    [dispatch],
  );
  const getQueryParamKeySuggestions = useCallback(
    async (value: string): Promise<string[]> =>
      getQuerySuggestions(value, completion => completion.split('=')[0] ?? ''),
    [getQuerySuggestions],
  );
  const getQueryParamValueSuggestions = useCallback(
    async (key: string, value: string): Promise<string[]> => {
      const keyPrefix = `${key}=`;
      const suggestions = await getQuerySuggestions(`${keyPrefix}${value}`, completion =>
        completion.startsWith(keyPrefix) ? completion.substring(keyPrefix.length) : '',
      );
      if ((suggestions.length && '*'.startsWith(value)) || '*' === value) {
        // also add wildcard result at the start if we have any suggestions and the value prefix would match it
        return ['*', ...suggestions];
      }
      return suggestions;
    },
    [getQuerySuggestions],
  );

  const [showFragment, setShowFragment] = React.useState(!!rule.fragment.value);
  useEffect(() => {
    // if a fragment didn't exist but now does, we will show the fragment field (due to outside updates)
    if (rule.fragment.value) {
      setShowFragment(true);
    }
  }, [rule.fragment.value]);

  const canAddFragment = hasFragmentEnabled && !showFragment;
  const handleAddFragment = useCallback(() => {
    setShowFragment(true);
  }, []);

  const onChangeFragment = useCallback(
    (fragment: string) => {
      onChange({
        ...rule,
        fragment: { value: fragment },
      });
    },
    [rule, onChange],
  );

  const onClearFragment = useCallback(() => {
    onChange({
      ...rule,
      fragment: { value: '' },
    });
    setShowFragment(false);
  }, [rule, onChange]);

  const getFragmentSuggestions = useCallback(
    (value: string): Promise<string[]> =>
      getSuggestions(
        rule.path.value,
        value,
        rule.domains.value,
        AutocompleteRequest.Scope.SCOPE_FRAGMENT,
      ),
    [getSuggestions, rule.path.value, rule.domains.value],
  );

  const handleRemove = onRemove
    ? () => {
        onRemove();
        activePattern?.deleteIndex(idx);
      }
    : undefined;

  const eventAggregation = useMemo((): { event: Event | null; aggregation?: Aggregation } => {
    const p = rule;
    const patternRule = p
      ? {
          domains: p.domains.parsed || [],
          path: p.path.parsed || [],
          fragment: p.fragment.parsed || [],
          query: p.query.parsed || [],
        }
      : undefined;
    const deps = makePagePatternDeps(patternRule, flags);
    // if there are query deps, we use visitedUrl instead of visitedPage to ensure we get appearances results
    // otherwise, we'd end up with no appearance metrics if the query param had not been configured before for a matching rule
    // but in this case we do need to aggregate on unique page views for relevant counts
    return deps.length
      ? patternRule?.query?.length
        ? {
            event: { primary: { visitedUrl: { any: {} } }, deps },
            aggregation: { unique: UniqueAggregation.UNIQUE_AGGREGATION_PAGEVIEW },
          }
        : { event: { primary: { visitedPage: { any: {} } }, deps } }
      : { event: null };
  }, [rule, flags]);

  const { event, aggregation } = useDebounce(eventAggregation, 800);

  return (
    <Pane
      borderRadius={4}
      border="standard"
      key={idx}
      className={classNames(css.urlRule, { [css.active]: active })}
    >
      {isML && <FullstoryManaged />}
      <Box padding={8} display="flex" flexDirection="column" gap={8}>
        <Box>
          <Flex justifyContent="flex-start">
            <Text appearance="bodyMediumBold">
              <FormattedMessage defaultMessage="URL Rule" id="+l6hC0" />
            </Text>
            <Box marginLeft={8} testId="info-icon">
              <Tooltip
                tooltipContent={UrlTooltip({
                  description: intl.formatMessage({
                    defaultMessage: 'URL Rules use a special syntax to describe dynamic url paths.',
                    id: '55kM1m',
                  }),
                  isCaseInsensitive,
                })}
                placement="right-start"
                variant="light"
                maxWidth={425}
              >
                <DeprecatedIcon name="information-secondary" color="neutrals-n10" size={16} />
              </Tooltip>
            </Box>
          </Flex>
        </Box>
        <URLRule
          hostname={{
            value: rule.domains.value,
            error: rule.domains.error,
            options: hostnameOptions,
            onBlur: handleHostnameChange,
            preventOverflow,
          }}
          path={{
            value: pathValue,
            error: rule.path.error ?? (!hasFragmentEnabled ? rule.fragment.error : undefined),
            onChange: handlePathChange,
            onFocus: handlePathFocus,
            onBlur: handlePathBlur,
            getSuggestions: getPathSuggestions,
          }}
          onClose={handleRemove}
          validationError={getValidationErrorForRule(rule, validationErrors)}
        />
        {rule.query.value.map((value, index) => (
          <URLRuleQueryParamAtIndex
            key={index}
            index={index}
            value={value}
            error={rule.query.error?.index === index ? rule.query.error : undefined}
            onChange={onChangeQueryParam}
            onClear={onClearQueryParam}
            onAnyFocus={setActive}
            getKeySuggestions={getQueryParamKeySuggestions}
            getValueSuggestions={getQueryParamValueSuggestions}
          />
        ))}
        {showFragment && !showFragmentInPath && (
          <URLRuleFragment
            value={rule.fragment.value}
            error={rule.fragment.error}
            onChange={onChangeFragment}
            onClear={onClearFragment}
            onFocus={setActive}
            getSuggestions={getFragmentSuggestions}
          />
        )}
        {(canAddQueryParam || canAddFragment) && (
          <Flex justifyContent="flex-start" gap={8}>
            {canAddQueryParam && (
              <Button
                variant="fill-builder"
                size="small"
                iconLeft="plus"
                onClick={handleAddQueryParam}
              >
                <FormattedMessage defaultMessage="Query Parameter" id="BZMZFS" />
              </Button>
            )}
            {canAddFragment && (
              <Button
                variant="fill-builder"
                size="small"
                iconLeft="plus"
                onClick={handleAddFragment}
              >
                <FormattedMessage defaultMessage="Fragment" id="35Zu7v" />
              </Button>
            )}
          </Flex>
        )}
      </Box>
      {event && Appearances && (
        <Box marginTop={4}>
          <Appearances
            event={event}
            queryParams={rule.query.value.map(q => q.key)}
            aggregation={aggregation}
            propertyOptions={appearancesPropertyOptions}
            onPreview={
              onPreview
                ? (primary, refinements, allAsRefinements) =>
                    onPreview(primary, refinements, allAsRefinements, idx)
                : undefined
            }
            objectType={intl.formatMessage({ defaultMessage: 'URL rule', id: 'KZYwKK' })}
            multiDimensionOverview={{
              propertyName: 'URL paths',
              subPropertyName: 'domains',
              overviewProperty: 'path',
              overviewSubProperty: 'host',
            }}
          />
        </Box>
      )}
    </Pane>
  );
};

export const URLRulesPatterns: FC<URLRulesPatternsProps> = ({
  value,
  onChange,
  hostnameOptions,
  getSuggestions,
  isML,
  validationErrors,
  errors = [],
  onConflictingRulesArchive,
  onConflictingRulesEdit,
  getPageById,
  isCaseInsensitive,
  preventOverflow,
}) => {
  if (!value.length) {
    // eslint-disable-next-line no-param-reassign
    value = [EMPTY_PATTERN];
  }

  const addRow = () => onChange([...value, EMPTY_PATTERN]);

  return (
    <Pane width="100%">
      {!!errors?.length && (
        <Box margin={[12, 0, 16, 0]}>
          <ConflictingRules
            errors={errors}
            onEdit={onConflictingRulesEdit}
            onArchive={onConflictingRulesArchive}
            getPageById={getPageById}
          />
        </Box>
      )}
      <Box display="flex" flexDirection="column" gap={16}>
        {value.map((rule, idx) => (
          <>
            <URLRuleRowAtIndex
              rule={rule}
              idx={idx}
              value={value}
              onChange={onChange}
              hostnameOptions={hostnameOptions}
              getSuggestions={getSuggestions}
              validationErrors={validationErrors}
              isML={isML}
              isCaseInsensitive={isCaseInsensitive}
              preventOverflow={preventOverflow}
            />
            <OrControl
              disabled={idx !== value.length - 1}
              testId={idx !== value.length - 1 ? 'or-divider' : 'or-button'}
              onClick={idx === value.length - 1 ? () => addRow() : undefined}
              omitVerticalPadding
            />
          </>
        ))}
      </Box>
    </Pane>
  );
};

type WithError<T extends { invalid: boolean }> = Omit<T, 'invalid'> & { error?: string };

interface URLRuleProps {
  hostname: WithError<ComponentProps<typeof HostnameSelect>>;
  path: WithError<ComponentProps<typeof URLRuleAutoComplete>>;
  onClose?: () => void;
  validationError?: string;
}

/** Component for editing a single URL rule. */
const URLRule: FC<URLRuleProps> = ({ hostname, path, onClose, validationError }) => {
  const intl = useIntl();
  const { error: hostnameError, ...hostnameProps } = hostname;
  const { error: pathError, ...pathProps } = path;

  return (
    <Pane width="100%" className={classNames(css.patternInput)} borderRadius={4}>
      {validationError && (
        <Box margin={[12, 0, 16, 0]}>
          <RuleWarning text={validationError} />
        </Box>
      )}
      <Flex justifyContent="flex-start" alignItems="flex-start">
        <Box paddingRight={8}>
          <HostnameSelect {...hostnameProps} invalid={!!hostnameError} />
          {hostnameError && <InputDescription invalid description={hostnameError} />}
        </Box>
        <Box flexGrow={1}>
          <URLRuleAutoComplete
            {...pathProps}
            invalid={!!pathError}
            placeholder={intl.formatMessage({
              defaultMessage: 'Enter a pathname...',
              id: 'XtTg21',
            })}
            testId="auto-complete-pathname"
          />
          {pathError && <InputDescription invalid description={pathError} />}
        </Box>
        {onClose && (
          <Box marginLeft={8} marginTop={8}>
            <IconButton
              title={intl.formatMessage({ defaultMessage: 'Remove URL Rule', id: 'HLGC+L' })}
              iconName="close"
              onClick={onClose}
            />
          </Box>
        )}
      </Flex>
    </Pane>
  );
};

interface URLRuleQueryParamAtIndex {
  index: number;
  value: { key: string; value: string };
  error?: { message: string; value: boolean };
  onChange: (index: number, value: { key: string; value: string }) => void;
  onClear: (index: number) => void;
  onAnyFocus?: () => void;
  getKeySuggestions: (value: string) => Promise<string[]>;
  getValueSuggestions: (key: string, value: string) => Promise<string[]>;
}

const URLRuleQueryParamAtIndex: React.FC<URLRuleQueryParamAtIndex> = ({
  index,
  value,
  error,
  onChange: onChangeAtIndex,
  onClear: onClearAtIndex,
  onAnyFocus,
  getKeySuggestions,
  getValueSuggestions,
}) => {
  const onChange = useCallback(
    (value: { key: string; value: string }) => {
      onChangeAtIndex(index, value);
    },
    [index, onChangeAtIndex],
  );
  const onClear = useCallback(() => {
    onClearAtIndex(index);
  }, [index, onClearAtIndex]);

  return (
    <URLRuleQueryParam
      value={value}
      error={error}
      onChange={onChange}
      onClear={onClear}
      onAnyFocus={onAnyFocus}
      getKeySuggestions={getKeySuggestions}
      getValueSuggestions={getValueSuggestions}
    />
  );
};
